// Step 2: Payer Selection

import { useState, useEffect } from 'react';
import { Check, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { WizardStepProps, SelectedPayer, SelectedPlan } from '@/lib/onboarding/types';
import { AVAILABLE_PAYERS, PLAN_TYPES, SUBMISSION_LANES } from '@/lib/onboarding/types';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';

export function Step2PayerSelection({ clientId, progress, onComplete, onBack }: WizardStepProps) {
  const { completeStep, isCompleting } = useClientOnboarding(clientId);
  const [selectedPayers, setSelectedPayers] = useState<SelectedPayer[]>(
    progress.selected_payers || []
  );
  const [expandedPayers, setExpandedPayers] = useState<Set<string>>(new Set());

  const togglePayer = (payerId: string, payerName: string) => {
    setSelectedPayers(prev => {
      const existing = prev.find(p => p.payer_id === payerId);
      if (existing) {
        return prev.filter(p => p.payer_id !== payerId);
      }
      return [...prev, {
        payer_id: payerId,
        payer_name: payerName,
        plans: [],
        submission_lane_priority: ['portal', 'fax', 'phone'],
      }];
    });
  };

  const togglePlan = (payerId: string, planType: string) => {
    setSelectedPayers(prev => prev.map(payer => {
      if (payer.payer_id !== payerId) return payer;
      
      const existingPlan = payer.plans.find(p => p.plan_type === planType);
      if (existingPlan) {
        return {
          ...payer,
          plans: payer.plans.filter(p => p.plan_type !== planType),
        };
      }
      
      const newPlan: SelectedPlan = {
        plan_id: `${payerId}_${planType.toLowerCase().replace(/\s+/g, '_')}`,
        plan_name: `${payer.payer_name} ${planType}`,
        plan_type: planType,
        selected_ruleset_template: null,
      };
      
      return {
        ...payer,
        plans: [...payer.plans, newPlan],
      };
    }));
  };

  const updateSubmissionPriority = (payerId: string, priority: string[]) => {
    setSelectedPayers(prev => prev.map(payer => {
      if (payer.payer_id !== payerId) return payer;
      return { ...payer, submission_lane_priority: priority };
    }));
  };

  const moveSubmissionLane = (payerId: string, index: number, direction: 'up' | 'down') => {
    const payer = selectedPayers.find(p => p.payer_id === payerId);
    if (!payer) return;
    
    const newPriority = [...payer.submission_lane_priority];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newPriority.length) return;
    
    [newPriority[index], newPriority[newIndex]] = [newPriority[newIndex], newPriority[index]];
    updateSubmissionPriority(payerId, newPriority);
  };

  const isPayerSelected = (payerId: string) => 
    selectedPayers.some(p => p.payer_id === payerId);

  const isPlanSelected = (payerId: string, planType: string) => {
    const payer = selectedPayers.find(p => p.payer_id === payerId);
    return payer?.plans.some(p => p.plan_type === planType) || false;
  };

  const hasValidSelection = selectedPayers.length > 0 && 
    selectedPayers.every(p => p.plans.length > 0);

  const handleSubmit = () => {
    completeStep({
      step: 2,
      stepData: {
        selected_payers: selectedPayers,
      },
    }, {
      onSuccess: onComplete,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payer Selection</CardTitle>
        <CardDescription>
          Select the payers and plans this client will work with. Configure submission lane priorities for each.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Payer list */}
        <div className="space-y-4">
          {AVAILABLE_PAYERS.map(payer => {
            const isSelected = isPayerSelected(payer.id);
            const isExpanded = expandedPayers.has(payer.id);
            const selectedPayerData = selectedPayers.find(p => p.payer_id === payer.id);

            return (
              <div
                key={payer.id}
                className={cn(
                  'border rounded-lg transition-colors',
                  isSelected ? 'border-primary bg-primary/5' : 'border-border'
                )}
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => togglePayer(payer.id, payer.name)}
                    />
                    <div>
                      <span className="font-medium">{payer.name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {payer.type === 'workers_comp' ? 'Workers Comp' : 'Commercial'}
                      </Badge>
                    </div>
                  </div>

                  {isSelected && (
                    <Collapsible
                      open={isExpanded}
                      onOpenChange={() => {
                        setExpandedPayers(prev => {
                          const next = new Set(prev);
                          if (next.has(payer.id)) {
                            next.delete(payer.id);
                          } else {
                            next.add(payer.id);
                          }
                          return next;
                        });
                      }}
                    >
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm">
                          {selectedPayerData?.plans.length || 0} plans selected
                          {isExpanded ? (
                            <ChevronDown className="ml-2 h-4 w-4" />
                          ) : (
                            <ChevronRight className="ml-2 h-4 w-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </Collapsible>
                  )}
                </div>

                {isSelected && isExpanded && (
                  <div className="border-t px-4 py-4 space-y-6">
                    {/* Plan selection */}
                    <div>
                      <h4 className="text-sm font-medium mb-3">Select Plans</h4>
                      <div className="flex flex-wrap gap-2">
                        {PLAN_TYPES[payer.id]?.map(planType => (
                          <Button
                            key={planType}
                            variant={isPlanSelected(payer.id, planType) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => togglePlan(payer.id, planType)}
                          >
                            {isPlanSelected(payer.id, planType) && (
                              <Check className="mr-1 h-3 w-3" />
                            )}
                            {planType}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Submission lane priority */}
                    <div>
                      <h4 className="text-sm font-medium mb-3">Submission Lane Priority</h4>
                      <p className="text-xs text-muted-foreground mb-2">
                        Drag to reorder. First available lane will be used.
                      </p>
                      <div className="space-y-2">
                        {selectedPayerData?.submission_lane_priority.map((laneId, idx) => {
                          const lane = SUBMISSION_LANES.find(l => l.id === laneId);
                          return (
                            <div
                              key={laneId}
                              className="flex items-center gap-2 p-2 bg-muted/50 rounded border"
                            >
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium w-6">{idx + 1}.</span>
                              <span className="text-sm flex-1">{lane?.label || laneId}</span>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  disabled={idx === 0}
                                  onClick={() => moveSubmissionLane(payer.id, idx, 'up')}
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  disabled={idx === selectedPayerData.submission_lane_priority.length - 1}
                                  onClick={() => moveSubmissionLane(payer.id, idx, 'down')}
                                >
                                  ↓
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Validation message */}
        {selectedPayers.length > 0 && !hasValidSelection && (
          <p className="text-sm text-destructive">
            Please select at least one plan for each selected payer.
          </p>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!hasValidSelection || isCompleting}
          >
            {isCompleting ? 'Saving...' : 'Continue to Epic Integration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
